import { Config } from '@/types/config';
import { Step } from '@/types/step';
import { date2str, str2date } from '@/utils/format';
import { addParamToTpl } from '@/utils/plugins/tpl';
import { safeParse } from '@/utils/json';
import { getAllValues } from '@/utils/shared';
import { $trl } from '@/utils/plugins/prompts';
import { applyMask } from '@/utils/mask';

export function getQuestionToSend (question) {
    if (['input'].includes(question.type)) {
        if (question.settings.trailingZeros > 0 && !isNaN(parseFloat(question.value))) {
            return parseFloat(question.value).toFixed(question.settings.trailingZeros);
        }

        return question.value === null ? '' : question.value;
    }

    if (['autoEmbeddingUrl'].includes(question.type)) {
        return window.location.href;
    }

    if (['redirectUrl'].includes(question.type)) {
        return question.value || '';
    }

    if (['checkbox'].includes(question.type)) {
        return question.value === 'true' || question.value === '1' || question.value === true;
    }

    if (['insuranceReview'].includes(question.type)) {
        // The block's answer is the reviewed plans array (primary-first,
        // images stripped) — sent as a deterministic JSON string.
        return question.value ? JSON.stringify(question.value) : '';
    }

    if (['uploadImage', 'signature', 'uploadFile'].includes(question.type)) {
        if (question.value && question.value.token) {
            return JSON.stringify({
                token: question.value.token,
                ...(question.value.text ? {
                    text: question.value.text,
                } : {}),
            });
        } else {
            if (question.value && question.value.base64) {
                return question.value.base64;
            } else {
                return question.value && '';
            }
        }
    }

    if (['providerslist', 'monthview'].includes(question.type)) {
        if (question.value && question.value.value) {
            return question.value.value;
        }

        if (question.value && typeof (question.value) === 'string') {
            return question.value;
        }
    }

    return question.value;
}

export function parseStepValues (step) {
    if (step.questions) {
        step.questions.forEach(question => {
            if (['checkboxList', 'linemenuList'].includes(question.type) ||
                (['combobox'].includes(question.type) && question.settings.multiple)) {
                question.value = safeParse(question.value);
            }
        });
    }

    return step;
}

export function addContextAttrs (appConfig: Config, template: string) {
    template = addParamToTpl(appConfig, template, 'context-question', 'value', 'false');
    template = addParamToTpl(appConfig, template, 'context-history', 'value', 'true');

    return template;
}

export let getQuestionTitle = (appConfig: Config, question) => {
    if (['providerslist', 'monthview'].includes(question.type)) {
        if (!question.value) {
            return '';
        }
        let content = document.createElement('div');
        content.innerHTML += question.value.cardInfo || '';
        content.innerHTML += question.value.info || '';

        return Array.from(content.querySelectorAll('history-title'))
            .map(element => element.innerHTML)
            .join(' ');
    }

    if (['address'].includes(question.type)) {
        return [
            $trl('aw.address_address_title'),
            question.settings.showApt ? $trl('aw.address_apt') : undefined,
            $trl('aw.address_locality'),
            $trl('aw.address_area'),
            $trl('aw.address_zip'),
        ].filter(v => !!v).join('<br/>');
    }

    return question.title !== undefined && addContextAttrs(appConfig, question.title);
};

function isKeptPlan (p) {
    return p && !p.removed && (p.name || p.fields?.payer);
}

function readInsuranceReviewValues (question) {
    // The answer is { action, insurances: [...] } (stringified on the wire
    // by getValue above); the generic fallback rendered the object as
    // "[object Object]". Summarize the plans the patient kept — payer
    // name, primary first (the answer is already primary-first), matching
    // the hub's own literal "Primary plan" tag.
    let answer = typeof question.value === 'string' ? safeParse(question.value) : question.value;
    let plans = answer && Array.isArray(answer.insurances) ? answer.insurances : [];
    return plans
        .filter(isKeptPlan)
        .map(p => {
            let name = p.name || p.fields.payer;
            return p.primary ? `${name} — Primary plan` : name;
        });
}

function readMatchedListValues (stateId, question) {
    return question.value.map(id => {
        let matched = getAllValues(stateId, question.id, question.values).find(item => item.id === id);
        return matched ? matched.title || matched.description || matched.match : '';
    });
}

// 282088: a history button rendered the literal "[object Object]" when an
// answer reached the generic value path as an object. Strings pass through
// unchanged. An object contributes its display field when it has one and
// nothing otherwise; a raw object is never stringified into the button.
export function toReadableValue (value): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value.map(toReadableValue).filter(v => v !== '').join(', ');
    }
    if (typeof value === 'object') {
        let display = ['title', 'description', 'match', 'name', 'label', 'input']
            .map(key => value[key])
            .find(v => v !== undefined && v !== null && v !== '');
        return toReadableValue(display);
    }
    return String(value);
}

function readMatchedValue (stateId, question) {
    let matched = getAllValues(stateId, question.id, question.values).find(item => item.id === question.value);
    return [matched ? (matched.title || matched.description || matched.match) : toReadableValue(question.value)];
}

export let getValuesReadable = (stateId: string, question) => {
    if (question.value === null || question.value === undefined) {
        return [];
    }

    if (['swipecard'].includes(question.type)) {
        return [$trl('aw.swipecard_value')];
    }

    if (['addressSearch'].includes(question.type)) {
        let value = safeParse(question.value) || {};
        return [value.input || ''];
    }

    if (['input'].includes(question.type)) {
        return [applyMask(question.value, question.settings.mask, question.settings.maskCharHolder)];
    }

    if (['address'].includes(question.type)) {
        let value = safeParse(question.value) || {};
        return Object.keys(value).map(key => value[key] || '&#8239;');
    }

    if (['uploadImage', 'signature'].includes(question.type)) {
        return [`<img class="odr-history-image" src="${question.value.base64}" height="21px"/>`];
    }

    if (['uploadFile'].includes(question.type)) {
        return [question.value.filename];
    }

    if (['insuranceReview'].includes(question.type)) {
        return readInsuranceReviewValues(question);
    }

    if (['date', 'datesimple'].includes(question.type)) {
        return [date2str(str2date(question.value), '#month %D %Y')];
    }

    if (['linemenuList', 'checkboxList'].includes(question.type)) {
        return readMatchedListValues(stateId, question);
    }

    if (['linemenu', 'combobox', 'select', 'radio', 'boxmenu'].includes(question.type)) {
        return readMatchedValue(stateId, question);
    }

    if (['providerslist', 'monthview'].includes(question.type)) {
        let content = document.createElement('div');
        content.innerHTML += question.value.cardInfo || '';
        content.innerHTML += question.value.info || '';

        let values = Array.from(content.querySelectorAll('history-value'))
            .map(element => element.innerHTML);

        return values;
    }

    if (question.value === false) {
        return [$trl('aw.no')];
    }

    if (question.value === true) {
        return [$trl('aw.yes')];
    }

    if (typeof question.value === 'string') {
        return [question.value];
    }

    let readable = toReadableValue(question.value);
    return readable === '' ? [] : [readable];
};

export function getHistoryContent (flowHistory: Step[]) {
    function getAdditionalHistory (additional, titleOrValue) {
        let content = document.createElement('div');
        content.innerHTML = additional.title;
        let clear = content.querySelector(`history-${titleOrValue}[clear="true"]`);

        let values = Array.from(content.querySelectorAll('history-' + titleOrValue))
            .map(element => element.innerHTML);

        return {
            clear: additional.clear || clear,
            items: values,
        };
    }

    let stepHistoryContent = {};

    flowHistory.forEach(step => {
        step.questions.forEach(question => {
            stepHistoryContent[question.id] = {
                title: question.title,
                clear: false,
                formId: false,
            };
        });
        stepHistoryContent = { ...stepHistoryContent, ...step.historyContent };
    });

    let historyContent = {};
    Object.keys(stepHistoryContent).forEach(id => {
        historyContent[id] = {
            form: stepHistoryContent[id].form,
            title: getAdditionalHistory(stepHistoryContent[id], 'title'),
            value: getAdditionalHistory(stepHistoryContent[id], 'value'),
        };
    });

    return historyContent;
}

export function getHistoryContentValues (historyContent, question, values) {
    if (historyContent[question.id] && historyContent[question.id].value.items.length) {
        if (historyContent[question.id].value.clear) {
            return historyContent[question.id].value.items;
        } else {
            return values.concat(historyContent[question.id].value.items);
        }
    } else {
        return values;
    }
}

export function getHistoryContentTitle (historyContent, question, title) {
    if (historyContent[question.id] && historyContent[question.id].title.items.length) {
        if (historyContent[question.id].title.clear) {
            return historyContent[question.id].title.items.join(' ');
        } else {
            return title + ' ' + historyContent[question.id].title.items.join(' ');
        }
    } else {
        return title;
    }
}
